import {
	AnyAction,
	combineReducers,
	configureStore,
	Middleware,
	Reducer,
} from '@reduxjs/toolkit'
import { connectivityReducer, ConnectivityState } from 'connectivity/redux'
import {
	eventSourceReducer,
	EventSourceState,
} from 'event-source-manager/redux'
import { knowledgeChecksReducer } from 'features/knowledge-checks'
import { questionsReducer } from 'features/questions'
import { screenShareReducer } from 'features/screen-share'
import { redux as scriptRedux } from 'features/script'
import { redux as slidesRedux } from 'features/slides'
import { redux as whiteboardsRedux } from 'features/whiteboard'
import { redux as notepadsRedux } from 'features/notepad'
import { groupEngagementReducer } from 'features/user-engagement'
import {
	redux as wordCloudRedux,
	WordCloudState,
} from 'features/pollings/word-cloud'
import {
	redux as multipleChoicePollingRedux,
	MultipleChoicePollingState,
} from 'features/pollings/multiple-choice-polling'
import {
	redux as listPollingRedux,
	ListPollingState,
} from 'features/pollings/list-polling'
import { interactionSchemeReducer } from 'interaction-scheme/redux'
import { roomReducer } from 'room/common/redux'
import { groupRoomReducer, GroupRoomState } from 'room/group-room/redux'
import { reducer as lobbyReducer } from 'room/lobby/redux'
import { rosterReducer, RosterState } from 'room/roster/redux'
import {
	trainingRoomReducer,
	TrainingRoomState,
} from 'room/training-room/redux'
import {
	middleware as sessionInfoMiddleware,
	reducer as sessionInfoRedux,
} from 'session-info/redux'
import { reducer as sessionModeReducer } from 'session-mode/redux'
import { reducer as sessionReducer, SessionState } from 'session/redux'
import { usersReducer } from 'users'
import { middleware as usersMiddleware, UsersState } from 'users/redux'
import { videoConferenceMediaReducer } from 'video-conference-media/redux'
import { workflowReducer, WorkflowsState } from 'workflow/redux'
import { ScreenShareState } from '../features/screen-share/screen-share-redux'
import {
	OrganizationState,
	organizationSlice as organizationReducer,
} from 'organization/redux'

import {
	redux as audioStreamingRedux,
	AudioStreamingState,
} from 'features/audio-streaming/audio-streaming-redux'
import {
	redux as youTubeStreamingRedux,
	YouTubeStreamingState,
} from 'features/youtube-streaming/youtube-streaming-redux'

import {
	State as SimpleGroupingState,
	reducer as simpleGroupingReducer,
} from 'features/simple-grouping/simple-grouping-redux'
import {
	ParamTogglerState,
	reducer as paramTogglerReducer,
} from 'features/param-toggler/param-toggler-redux'
import { presenterReducer } from 'presenter-mode/redux'

/**
 * All this casting here (as Reducer<>) is needed
 * because the redux-toolkit has some issues when
 * reducer action types don't include action
 * without payload. Maybe they will fix it in the future.
 *
 * It should not break any type checking, please just don't
 * use AnyAction anywhere else.
 */
export const appReducer = combineReducers({
	workflow: workflowReducer as Reducer<WorkflowsState, AnyAction>,
	interactionScheme: interactionSchemeReducer,
	lobby: lobbyReducer,
	roster: rosterReducer as Reducer<RosterState, AnyAction>,
	room: roomReducer,
	trainingRoom: trainingRoomReducer as Reducer<TrainingRoomState, AnyAction>,
	groupRoom: groupRoomReducer as Reducer<GroupRoomState, AnyAction>,
	simpleGrouping: simpleGroupingReducer as Reducer<
		SimpleGroupingState,
		AnyAction
	>,
	sessionInfo: sessionInfoRedux,
	sessionMode: sessionModeReducer,
	// sessionInfoReducer as Reducer<SessionPlanningState, AnyAction>,
	videoConferenceMedia: videoConferenceMediaReducer,
	eventSource: eventSourceReducer as Reducer<EventSourceState, AnyAction>,
	connectivity: connectivityReducer as Reducer<ConnectivityState, AnyAction>,
	questions: questionsReducer,
	screenShare: screenShareReducer as Reducer<ScreenShareState, AnyAction>,
	slides: slidesRedux.reducer,
	whiteboards: whiteboardsRedux.reducer,
	notepads: notepadsRedux.reducer,
	wordCloud: wordCloudRedux.reducer as Reducer<WordCloudState, AnyAction>,
	multipleChoicePolling: multipleChoicePollingRedux.reducer as Reducer<
		MultipleChoicePollingState,
		AnyAction
	>,
	listPolling: listPollingRedux.reducer as Reducer<ListPollingState, AnyAction>,
	knowledgeChecks: knowledgeChecksReducer,
	users: usersReducer as Reducer<UsersState, AnyAction>,
	session: sessionReducer as Reducer<SessionState, AnyAction>,
	groupEngagement: groupEngagementReducer,
	script: scriptRedux.reducer,
	organization: organizationReducer.reducer as Reducer<
		OrganizationState,
		AnyAction
	>,
	audioStreaming: audioStreamingRedux.reducer as Reducer<
		AudioStreamingState,
		AnyAction
	>,
	youtubeStreaming: youTubeStreamingRedux.reducer as Reducer<
		YouTubeStreamingState,
		AnyAction
	>,
	paramToggler: paramTogglerReducer as Reducer<ParamTogglerState, AnyAction>,
	presenter: presenterReducer,
})

export type RootState = ReturnType<typeof appReducer>

/** The middlewares that fetch when eventbus events arrive. */
export const FETCHING_MIDDLEWARES: Middleware[] = [
	usersMiddleware,
	sessionInfoMiddleware,
]

type StoreParts = {
	reducer?: Reducer<RootState, AnyAction>
	/** After the defaults; `FETCHING_MIDDLEWARES` unless given. */
	middleware?: Middleware[]
}

const configureRootStore = ({
	reducer = appReducer,
	middleware = FETCHING_MIDDLEWARES,
}: StoreParts) =>
	configureStore({
		reducer,
		middleware: getDefaultMiddleware =>
			getDefaultMiddleware({
				serializableCheck: false,
				immutableCheck: false,
			}).concat(middleware),
	})

export type AppStore = ReturnType<typeof configureRootStore>

export type AppDispatch = AppStore['dispatch']

/**
 * The live-session store. Built once, by the app's entry point, for the
 * window it runs in (presenter mode's `window-store.ts`).
 */
export const buildRootStore = (parts: StoreParts = {}): AppStore =>
	configureRootStore(parts)
